import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, CircularProgress, Typography } from '@mui/material';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { formatWashboardTimestamp } from 'utils/formatTimestamp';
import { useWashboardDeviceScreenCapture } from 'api/queries/useWashboardDeviceScreenCapture';
import './DeviceScreen.scss';

type DeviceScreenProps = {
  deviceId: number;
};

const DeviceScreen: React.FC<DeviceScreenProps> = ({ deviceId }) => {
  const { data, isLoading, isFetching, isError, error, refetch } =
    useWashboardDeviceScreenCapture(deviceId);

  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  const captureUrl = data?.url ?? null;
  const lastRefreshedAt = data?.lastRefreshedAt ?? null;

  useEffect(() => {
    setHasImageLoadError(false);
  }, [captureUrl]);

  if (isLoading) {
    return (
      <div className="device-screen centered">
        <CentsLoader />
      </div>
    );
  }

  const requestError = isError
    ? error?.response?.data?.error ?? 'Failed to capture device screen'
    : null;

  const showImage = Boolean(captureUrl) && !hasImageLoadError;

  return (
    <div className="device-screen">
      <div className="screen-toolbar">
        <div className="screen-status">
          {lastRefreshedAt && (
            <Typography variant="body2" className="screen-timestamp">
              {`Screen last refreshed ${formatWashboardTimestamp(lastRefreshedAt)}`}
            </Typography>
          )}
        </div>
        <Button
          variant="outlined"
          startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </div>

      {requestError && (
        <div className="screen-error" role="alert">
          {requestError}
        </div>
      )}

      <div className="screen-frame">
        {showImage && (
          <img
            src={captureUrl as string}
            alt="Device screen capture"
            className="screen-image"
            onError={() => setHasImageLoadError(true)}
          />
        )}

        {!showImage && hasImageLoadError && (
          <div className="screen-empty">Unable to load the screen capture image.</div>
        )}

        {!showImage && !hasImageLoadError && requestError && (
          <div className="screen-empty">No screen capture available for this device.</div>
        )}

        {!showImage && !hasImageLoadError && !requestError && (
          <div className="screen-empty">Device screen capture not found.</div>
        )}
      </div>
    </div>
  );
};

export default DeviceScreen;
